import { Injectable } from '@angular/core';
// import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ExamdojoAuthService } from '@examdojo/auth';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { InternationalizationService } from '@examdojo/core/i18n';
import { mapToVoid, rethrowError } from '@examdojo/core/rxjs';
import { ToastService } from '@examdojo/core/toast';
import { USER_UPDATE_ALLOWED_KEYS, UserHttpModel, UserStoreModel, UserUpdateModel } from '@examdojo/models/user';
import { RealtimeChangesListenEvent, RealtimeService, TableUpdateModel } from '@examdojo/supabase';
import { sanitizeObject } from '@examdojo/util/sanitize-object';
import { RealtimePostgresDeletePayload } from '@supabase/supabase-js';
import { catchError, concatMap, distinctUntilChanged, EMPTY, filter, map, of, switchMap, tap } from 'rxjs';
import { isPostgrestError } from '@examdojo/models/error-handler';
import { IS_PROD_BUILD } from '@examdojo/core/environment';
import { UserHttpService } from './user-http.service';
import { UserQuery } from './user.query';
import { UserStore } from './user.store';

@Injectable({ providedIn: 'root' })
export class UserService {
  constructor(
    private readonly userHttpService: UserHttpService,
    private readonly userStore: UserStore,
    private readonly userQuery: UserQuery,
    private readonly authService: ExamdojoAuthService,
    private readonly realtimeService: RealtimeService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly toastService: ToastService,
    private readonly internationalizationService: InternationalizationService,
  ) {
    // TODO: enable when BE will implement removing of public.users
    // this.listenForDeletedProfileEvent().pipe(takeUntilDestroyed()).subscribe();

    this.userQuery
      .selectActiveId()
      .pipe(
        tap((activeUserId) => {
          if (!activeUserId) {
            this.userStore.reset();
          }
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  fetchCurrentUserRole() {
    // TODO: Consider proxying the role from auth service (currentUser$ query) instead of storing it in the user store
    return this.authService.currentUser$.pipe(
      filter(Boolean),
      switchMap(() => this.authService.selectUserRole()),
      tap((role) => {
        this.userStore.updateProps((props) => ({
          ...props,
          currentUserRole: role,
        }));
      }),
      this.errorHandlerService.catchError('[UserService]: Failed to fetch current user role', rethrowError(), {
        toast: 'Failed to fetch current user role',
      }),
    );
  }

  uploadProfilePicture(picture: File | ArrayBuffer | Blob, fileType: string, fileName: string) {
    return this.userHttpService.uploadProfilePicture(picture, fileType, fileName);
  }

  deleteProfilePicture(fileName: string) {
    return this.userHttpService.deleteProfilePicture(fileName);
  }

  fetchActiveUserProfile() {
    return this.userHttpService.fetchActiveUserProfile().pipe(
      map((data) => this.mapUserHttpModelToStoreModel(data)),
      tap((user) => {
        this.userStore.set([user]);
        this.userStore.setActive(user.id);
      }),
      catchError((error: unknown) => {
        if (!IS_PROD_BUILD && isPostgrestError(error) && error.message === 'No suitable key or wrong key type') {
          this.authService.handleLogout();
          return EMPTY;
        }
        throw error;
      }),
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'user' }),
    );
  }

  update(id: UserStoreModel['id'], updateModel: UserUpdateModel) {
    const user = this.mapUpdateModelToHttpUpdateModel(updateModel);

    return this.userHttpService.update(id, user).pipe(
      tap((data) => {
        this.userStore.update(data.id, this.mapUserHttpModelToStoreModel(data));
      }),
      mapToVoid(),
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'user', action: 'update' }),
    );
  }

  invite(invitationData: { email: string }) {
    return this.userHttpService.invite(invitationData);
  }

  fetchUsersByIds(userIds: string[]) {
    return this.userHttpService.fetchByIds(userIds).pipe(
      tap((users) => {
        users.forEach((user) => {
          this.userStore.upsert(user.id, this.mapUserHttpModelToStoreModel(user));
        });
      }),
      mapToVoid(),
    );
  }

  deleteProfile() {
    return this.userHttpService.deleteProfile().pipe(
      tap(() => {
        if (this.userQuery.getActiveId()) {
          this.handleDeleteProfile();
        }
      }),
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'user', action: 'delete' }),
    );
  }

  private listenForDeletedProfileEvent() {
    return this.userQuery.selectActiveId().pipe(
      distinctUntilChanged(),
      switchMap((userId) => {
        if (!userId) {
          return of([]);
        }

        return this.realtimeService
          .listenRealtime<UserHttpModel>({
            schema: 'public',
            table: 'users',
            event: RealtimeChangesListenEvent.DELETE,
          })
          .pipe(
            map((payload) => payload as RealtimePostgresDeletePayload<UserHttpModel>),
            concatMap((payload) => {
              if (
                payload.eventType === RealtimeChangesListenEvent.DELETE &&
                payload.old?.id &&
                this.userQuery.getActiveId() === payload.old.id
              ) {
                this.handleDeleteProfile();
              }

              return of();
            }),
          );
      }),
    );
  }

  private handleDeleteProfile() {
    this.userStore.reset();
    this.authService.handleLogout();
    this.toastService.success(this.internationalizationService.translate('user_removed'));
  }

  private mapUserHttpModelToStoreModel(questionHttpModel: UserHttpModel): UserStoreModel {
    return {
      ...questionHttpModel,
      origin: questionHttpModel.origin as unknown as UserStoreModel['origin'],
    };
  }

  private mapUpdateModelToHttpUpdateModel(updateModel: UserUpdateModel): TableUpdateModel<'users'> {
    return sanitizeObject(updateModel, USER_UPDATE_ALLOWED_KEYS as never[]);
  }
}
